import { useCallback, useEffect, useRef, useState } from 'react';
const SPEECH_SILENCE_MS = 10000;
const SPEECH_RESTART_MS = 150;
export function useSpeechRecognition(value, onValueChange) {
    const [isListening, setIsListening] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [supported, setSupported] = useState(true);
    const recognitionRef = useRef(null);
    const sessionRef = useRef(null);
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = value;
    }, [value]);
    const clearTimers = useCallback((session) => {
        if (!session)
            return;
        if (session.silenceTimer)
            clearTimeout(session.silenceTimer);
        if (session.restartTimer)
            clearTimeout(session.restartTimer);
        session.silenceTimer = null;
        session.restartTimer = null;
    }, []);
    const finalizeSession = useCallback((updateValue = true) => {
        const session = sessionRef.current;
        if (!session) {
            setIsListening(false);
            setStatusText('');
            return;
        }
        if (updateValue) {
            onValueChange(`${session.baseText}${session.committed}`.trim());
        }
        clearTimers(session);
        sessionRef.current = null;
        setIsListening(false);
        setStatusText('');
    }, [clearTimers, onValueChange]);
    const resetSilenceTimer = useCallback((session) => {
        if (session.silenceTimer)
            clearTimeout(session.silenceTimer);
        session.silenceTimer = setTimeout(() => {
            session.userActive = false;
            try {
                recognitionRef.current?.stop();
            }
            catch {
                finalizeSession();
            }
        }, SPEECH_SILENCE_MS);
    }, [finalizeSession]);
    const updateTranscript = useCallback((session, interimText = '') => {
        onValueChange(`${session.baseText}${session.committed}${interimText}`);
    }, [onValueChange]);
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setSupported(false);
            setStatusText('Speech recognition is not supported in this browser. Use Chrome or Edge.');
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onstart = () => {
            setIsListening(true);
            setStatusText('Listening... you can pause for up to 10 seconds.');
        };
        recognition.onend = () => {
            const session = sessionRef.current;
            if (session?.userActive) {
                session.restartTimer = setTimeout(() => {
                    if (!sessionRef.current?.userActive)
                        return;
                    try {
                        recognition.start();
                    }
                    catch {
                        session.userActive = false;
                        finalizeSession();
                    }
                }, SPEECH_RESTART_MS);
                return;
            }
            finalizeSession();
        };
        recognition.onerror = (event) => {
            if (event.error === 'aborted')
                return;
            if (event.error === 'no-speech' && sessionRef.current?.userActive) {
                resetSilenceTimer(sessionRef.current);
                return;
            }
            setStatusText(`Speech recognition error: ${event.error}`);
            if (sessionRef.current)
                sessionRef.current.userActive = false;
        };
        recognition.onresult = (event) => {
            const session = sessionRef.current;
            if (!session)
                return;
            resetSilenceTimer(session);
            let interimText = '';
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    session.committed += transcript;
                }
                else {
                    interimText += transcript;
                }
            }
            updateTranscript(session, interimText);
        };
        recognitionRef.current = recognition;
        return () => {
            sessionRef.current = null;
            try {
                recognition.stop();
            }
            catch {
                // ignore
            }
        };
    }, [finalizeSession, resetSilenceTimer, updateTranscript]);
    const toggleListening = useCallback(() => {
        const recognition = recognitionRef.current;
        if (!recognition)
            return;
        if (isListening) {
            if (sessionRef.current)
                sessionRef.current.userActive = false;
            try {
                recognition.stop();
            }
            catch {
                finalizeSession();
            }
            return;
        }
        const existing = valueRef.current.trim();
        const session = {
            userActive: true,
            baseText: existing ? `${existing} ` : '',
            committed: '',
            silenceTimer: null,
            restartTimer: null,
        };
        sessionRef.current = session;
        resetSilenceTimer(session);
        try {
            recognition.start();
        }
        catch (error) {
            sessionRef.current = null;
            setStatusText(`Could not start microphone: ${error instanceof Error ? error.message : 'unknown error'}`);
        }
    }, [finalizeSession, isListening, resetSilenceTimer]);
    const stopListening = useCallback((options) => {
        const session = sessionRef.current;
        const discard = options?.discardTranscript ?? false;
        if (session) {
            session.userActive = false;
            clearTimers(session);
            if (discard) {
                sessionRef.current = null;
                setIsListening(false);
                setStatusText('');
            }
        }
        try {
            recognitionRef.current?.stop();
        }
        catch {
            if (!discard) {
                finalizeSession(true);
            }
        }
    }, [clearTimers, finalizeSession]);
    const discardListening = useCallback(() => {
        stopListening({ discardTranscript: true });
    }, [stopListening]);
    return {
        isListening,
        statusText,
        supported,
        toggleListening,
        stopListening,
        discardListening,
    };
}
